import API from "@/services/api";
import axios from "axios";
import toast from "react-hot-toast";
import { authService } from "@/services/auth.service";
import { setupAuthInterceptor } from "@/services/auth-interceptor";
import { isAcceptancePolicyEnvelope } from "@/domain/order/acceptance-policy-envelope";
import { storefrontHref } from "@/utils/storefront-paths";

let interceptorsRegistered = false;

export const setupInterceptors = (): void => {
  if (interceptorsRegistered) return;

  // Registered first — runs after auth interceptor on rejection chain.
  API.interceptors.response.use(
    (response) => response,
    async (error) => {
      const status = error?.response?.status;
      const config = error.config as
        | { _authRetry?: boolean; url?: string; skipErrorToast?: boolean }
        | undefined;
      const envelope = error?.response?.data as
        | { resultCode?: unknown; reason?: unknown; data?: unknown }
        | undefined;
      // Order-acceptance policies are handled by checkout callers — never generic toast.
      const isAcceptancePolicy = isAcceptancePolicyEnvelope(envelope);

      if (status === 401) {
        const refreshAttempted = Boolean(config?._authRetry);
        const isRefreshUrl = config?.url?.includes("auth/v1/refresh");

        if (refreshAttempted || isRefreshUrl) {
          toast.error("Session expired. Redirecting to login...");
          await authService.invalidateSession();
          setTimeout(() => {
            window.location.href = storefrontHref("/login");
          }, 1500);
        }
        return Promise.reject(error);
      }

      // Expected business policy — callers handle OrderAcceptancePolicyError / unpaid sheet.
      if (isAcceptancePolicy || config?.skipErrorToast) {
        return Promise.reject(error);
      }

      if (error.message?.includes("Network Error")) {
        toast.error("No Internet connection.");
      } else if (error.message?.includes("net::ERR_NETWORK_CHANGED")) {
        toast.error("Network Error, Please try again.");
      } else if (axios.isCancel(error)) {
        if (process.env.NODE_ENV === "development") {
          // Expected during navigation / SafeQuery abort — not a user-facing failure.
          // eslint-disable-next-line no-console -- dev-only diagnostic for cancelled requests
          console.debug(
            "[API] Request cancelled:",
            error.config?.url ?? "(unknown url)"
          );
        }
        return Promise.reject(error);
      } else if (!error.config?.url?.includes("auth/v1/")) {
        toast.error("Something bad at our end.");
      }

      return Promise.reject(error);
    }
  );

  // Registered last — runs first on rejection chain (axios LIFO).
  setupAuthInterceptor();

  interceptorsRegistered = true;
};
